import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookOpen, Plus, Trash2, FileText, Send, TrendingUp, WalletCards } from "lucide-react";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

const validTabs = ["input", "daily", "pnl", "journal", "coa"];

export default function Finance() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(requestedTab) ? requestedTab : "daily"
  );

  useEffect(() => {
    const requested = searchParams.get("tab");
    if (validTabs.includes(requested)) setActiveTab(requested);
  }, [searchParams]);

  const changeFinanceTab = (value) => {
    if (!validTabs.includes(value)) return;
    setActiveTab(value);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    }, { replace: true });
  };

  return (
    <div className="page-shell" data-layered-navigation="finance">
      <div data-workspace-redundant-header>
        <h1 className="text-2xl font-extrabold font-display flex items-center gap-2"><BookOpen className="w-6 h-6 text-orange-500"/>Finance & Accounting</h1>
        <p className="text-sm text-slate-500">Jurnal harian outlet, Chart of Accounts, Laba Rugi, dan laporan harian ke WhatsApp</p>
      </div>
      <Tabs value={activeTab} onValueChange={changeFinanceTab}>
        <TabsList data-sticky-page-tabs="finance">
          <TabsTrigger value="input" data-testid="tab-input"><WalletCards className="w-4 h-4 mr-1"/>Input Transaksi</TabsTrigger>
          <TabsTrigger value="pnl" data-testid="tab-pnl"><TrendingUp className="w-4 h-4 mr-1"/>Laba Rugi</TabsTrigger>
        </TabsList>
        <TabsContent value="input"><SimpleFinanceInput/></TabsContent>
        <TabsContent value="daily"><DailyReport/></TabsContent>
        <TabsContent value="pnl"><PnLTab/></TabsContent>
        <TabsContent value="journal"><JournalTab/></TabsContent>
        <TabsContent value="coa"><COATab/></TabsContent>
      </Tabs>
    </div>
  );
}


function SimpleFinanceInput() {
  const [options, setOptions] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [entryType, setEntryType] = useState("expense");
  const [preset, setPreset] = useState("");
  const [outletId, setOutletId] = useState("");
  const [source, setSource] = useState("1000");
  const [destination, setDestination] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/finance/input-options"), api.get("/outlets")]).then(([a, o]) => {
      setOptions(a.data);
      setOutlets(o.data || []);
      if (a.data?.cash_accounts?.[0]) setSource(a.data.cash_accounts[0].code);
      if (o.data?.[0]) setOutletId(o.data[0].id);
    }).catch(() => toast.error("Gagal memuat pilihan transaksi"));
  }, []);

  const presets = options?.presets?.[entryType] || [];
  useEffect(() => {
    if (presets.length && !presets.includes(preset)) setPreset(presets[0]);
    if (!presets.length) setPreset("");
  }, [entryType, options]); // eslint-disable-line

  const cashAccounts = options?.cash_accounts || [];
  const allAccounts = options?.accounts || [];
  const isTransfer = entryType === "transfer";

  async function save() {
    const numeric = Number(String(amount).replace(/[^0-9]/g, ""));
    if (!preset || numeric <= 0) return toast.error("Pilih tujuan dan isi jumlah transaksi");
    if (!source) return toast.error("Pilih rekening");
    if (isTransfer && !destination) return toast.error("Pilih rekening tujuan");
    setSaving(true);
    try {
      const { data } = await api.post("/finance/simple-entry", {
        date, outlet_id: outletId || null, entry_type: entryType, preset,
        amount: numeric, source_account_code: source,
        destination_account_code: destination || null, notes
      });
      toast.success(`Tersimpan · ${rupiah(data.entry.total)} · jurnal balance`);
      setAmount(""); setNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transaksi gagal disimpan");
    } finally { setSaving(false); }
  }

  if (!options) return <div className="mt-4 text-slate-400 text-center py-8">Memuat formulir...</div>;

  return (
    <div className="mt-4 max-w-3xl space-y-4">
      <Card className="p-5">
        <div className="mb-5">
          <h2 className="text-lg font-bold">Input transaksi sederhana</h2>
          <p className="text-sm text-slate-500">Pilih kejadian bisnis. Sistem otomatis membuat jurnal akuntansi di belakang layar.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-xs font-semibold">Tanggal</label><Input data-testid="simple-date" type="date" value={date} onChange={e=>setDate(e.target.value)}/></div>
          <div><label className="text-xs font-semibold">Outlet</label>
            <Select value={outletId || "none"} onValueChange={v=>setOutletId(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="simple-outlet"><SelectValue placeholder="Pilih outlet"/></SelectTrigger>
              <SelectContent><SelectItem value="none">Tanpa outlet</SelectItem>{outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.brand} · {o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-semibold">Apa yang terjadi?</label>
            <Select value={entryType} onValueChange={v=>{setEntryType(v);setPreset("");setDestination("");}}>
              <SelectTrigger data-testid="simple-type"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">Pengeluaran</SelectItem>
                <SelectItem value="income">Pemasukan</SelectItem>
                <SelectItem value="stock_purchase">Belanja bahan / stok</SelectItem>
                <SelectItem value="supplier_payment">Bayar hutang supplier</SelectItem>
                <SelectItem value="payroll_payment">Bayar hutang gaji</SelectItem>
                <SelectItem value="employee_advance">Kasbon karyawan</SelectItem>
                <SelectItem value="owner_draw">Prive pemilik</SelectItem>
                <SelectItem value="transfer">Pindah rekening</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-semibold">Tujuan / kategori</label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="simple-preset"><SelectValue placeholder="Pilih tujuan"/></SelectTrigger>
              <SelectContent>{presets.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs font-semibold">Rekening sumber</label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger data-testid="simple-source"><SelectValue/></SelectTrigger>
              <SelectContent>{cashAccounts.map(a=><SelectItem key={a.code} value={a.code}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isTransfer && <div><label className="text-xs font-semibold">Rekening tujuan</label>
            <Select value={destination} onValueChange={setDestination}>
              <SelectTrigger data-testid="simple-destination"><SelectValue placeholder="Pilih rekening tujuan"/></SelectTrigger>
              <SelectContent>{cashAccounts.filter(a=>a.code!==source).map(a=><SelectItem key={a.code} value={a.code}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          <div><label className="text-xs font-semibold">Jumlah</label><Input data-testid="simple-amount" inputMode="numeric" placeholder="Rp 0" value={amount} onChange={e=>setAmount(e.target.value)}/></div>
          <div><label className="text-xs font-semibold">Keterangan <span className="font-normal text-slate-400">(opsional)</span></label><Input data-testid="simple-notes" placeholder="Contoh: beli CC, coklat, keju..." value={notes} onChange={e=>setNotes(e.target.value)}/></div>
        </div>
        <div className="mt-5 flex items-center justify-between gap-3 rounded-lg bg-slate-50 p-3">
          <div className="text-xs text-slate-500">Tidak perlu memilih debit/kredit. Sistem akan menentukan akun otomatis.</div>
          <Button data-testid="simple-save" onClick={save} disabled={saving}>{saving ? "Menyimpan..." : "Simpan transaksi"}</Button>
        </div>
      </Card>
      <Card className="p-4 bg-emerald-50 border-emerald-100">
        <div className="text-sm font-semibold text-emerald-800">Contoh</div>
        <div className="text-xs text-emerald-700 mt-1">Belanja bahan / stok → Uang Tunai → Rp359.000 → sistem mencatat Persediaan bertambah dan Kas berkurang.</div>
      </Card>
    </div>
  );
}

function PnLTab() {
  const [period, setPeriod] = useState("30d");
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/outlets").then(({data})=>setOutlets(data)); }, []);
  useEffect(() => {
    const q = new URLSearchParams({ period, ...(outletId && { outlet_id: outletId }) }).toString();
    api.get(`/reports/pnl-full?${q}`).then(({data})=>setData(data)).catch(()=>setData(null));
  }, [period, outletId]);

  if (!data) return <div className="mt-4 text-slate-400 text-center py-8">Memuat...</div>;

  return (
    <div className="mt-4 space-y-4">
      <Card className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><label className="text-xs">Periode</label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="pnl-period-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Hari ini</SelectItem>
              <SelectItem value="yesterday">Kemarin</SelectItem>
              <SelectItem value="7d">7 hari</SelectItem>
              <SelectItem value="30d">30 hari</SelectItem>
              <SelectItem value="month">Bulan berjalan</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><label className="text-xs">Outlet</label>
          <Select value={outletId||"all"} onValueChange={v=>setOutletId(v==="all"?"":v)}>
            <SelectTrigger data-testid="pnl-outlet-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua outlet</SelectItem>
              {outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.brand} · {o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end text-xs text-slate-500">Periode WIB: <b className="ml-1">{data.period?.wib_from} → {data.period?.wib_to}</b></div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4"><div className="text-xs text-slate-500">Pendapatan Bersih</div><div className="text-2xl font-extrabold num text-emerald-600" data-testid="pnl-revenue">{rupiah(data.revenue?.net)}</div><div className="text-xs text-slate-400">Gross {rupiah(data.revenue?.gross)} - Diskon {rupiah(data.revenue?.discount)}</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500">HPP / COGS</div><div className="text-2xl font-extrabold num text-amber-600">{rupiah(data.cogs?.total)}</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500">Gross Profit</div><div className="text-2xl font-extrabold num text-blue-600" data-testid="pnl-gp">{rupiah(data.gross_profit)}</div><div className="text-xs text-slate-400">Margin {data.gross_margin_pct}%</div></Card>
        <Card className={`p-4 ${data.net_income<0?"bg-rose-50":""}`}><div className="text-xs text-slate-500">Net Income</div><div className={`text-2xl font-extrabold num ${data.net_income<0?"text-rose-600":"text-emerald-600"}`} data-testid="pnl-net">{rupiah(data.net_income)}</div><div className="text-xs text-slate-400">Margin {data.net_margin_pct}%</div></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="font-semibold mb-2 text-emerald-700">Revenue</div>
          <table className="w-full text-sm">
            <tbody>
              {(data.revenue?.lines||[]).map(l=>(<tr key={l.code} className="border-b"><td className="py-1 text-xs font-mono text-slate-400 w-14">{l.code}</td><td>{l.name}</td><td className="text-right num">{rupiah(l.amount)}</td></tr>))}
              {(data.revenue?.discount_lines||[]).map(l=>(<tr key={l.code} className="border-b text-rose-600"><td className="py-1 text-xs font-mono w-14">{l.code}</td><td>{l.name}</td><td className="text-right num">- {rupiah(l.amount)}</td></tr>))}
              {!data.revenue?.lines?.length && !data.revenue?.discount_lines?.length && <tr><td colSpan="3" className="py-4 text-center text-slate-400">-</td></tr>}
            </tbody>
          </table>
        </Card>
        <Card className="p-4">
          <div className="font-semibold mb-2 text-amber-700">Biaya & COGS</div>
          <table className="w-full text-sm">
            <tbody>
              {(data.cogs?.lines||[]).map(l=>(<tr key={l.code} className="border-b"><td className="py-1 text-xs font-mono text-slate-400 w-14">{l.code}</td><td>{l.name}</td><td className="text-right num">{rupiah(l.amount)}</td></tr>))}
              {(data.expenses?.lines||[]).map(l=>(<tr key={l.code} className="border-b"><td className="py-1 text-xs font-mono text-slate-400 w-14">{l.code}</td><td>{l.name}</td><td className="text-right num">{rupiah(l.amount)}</td></tr>))}
              {!data.cogs?.lines?.length && !data.expenses?.lines?.length && <tr><td colSpan="3" className="py-4 text-center text-slate-400">-</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
      <div className="text-xs text-slate-400 text-right">{data.entries_scanned} jurnal dianalisis</div>
    </div>
  );
}

function DailyReport() {
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [phone, setPhone] = useState("");

  useEffect(() => { api.get("/outlets").then(({ data }) => { setOutlets(data); if (data[0]) setOutletId(data[0].id); }); }, []);
  useEffect(() => {
    if (!outletId) return;
    api.get(`/reports/daily?outlet_id=${outletId}&date=${date}`).then(({ data }) => setReport(data));
  }, [outletId, date]);

  async function sendWA() {
    if (!outletId) return;
    const { data } = await api.get(`/reports/daily/wa?outlet_id=${outletId}&date=${date}&phone=${encodeURIComponent(phone)}`);
    window.open(data.wa_link, "_blank");
    toast.success("Laporan siap dikirim ke WhatsApp");
  }

  if (!report) return <div className="mt-4 text-slate-400 py-8 text-center">Memuat...</div>;

  return (
    <div className="mt-4 space-y-4">
      <Card className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div><label className="text-xs">Outlet</label>
          <Select value={outletId} onValueChange={setOutletId}>
            <SelectTrigger data-testid="daily-outlet-select"><SelectValue/></SelectTrigger>
            <SelectContent>{outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.brand} · {o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs">Tanggal</label><Input data-testid="daily-date-input" type="date" value={date} onChange={e=>setDate(e.target.value)}/></div>
        <div><label className="text-xs">No. WA Owner</label><Input data-testid="daily-phone-input" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="62812xxxx"/></div>
        <div className="flex items-end"><Button data-testid="daily-send-wa-btn" onClick={sendWA} className="w-full bg-emerald-500 hover:bg-emerald-600"><Send className="w-4 h-4 mr-1"/>Kirim WhatsApp</Button></div>
      </Card>

      <Card className="p-6">
        <div className="text-center mb-4">
          <div className="text-2xl font-extrabold font-display">{report.outlet?.brand}</div>
          <div className="text-sm text-slate-500">{report.outlet?.name}</div>
          <div className="text-sm">Laporan Harian · <b>{report.date}</b></div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="p-3 rounded bg-blue-50"><div className="text-xs text-blue-700">Modal Awal</div><div className="font-bold num">{rupiah(report.opening_cash)}</div></div>
          <div className="p-3 rounded bg-emerald-50"><div className="text-xs text-emerald-700">Total Omzet</div><div className="font-bold num text-emerald-700">{rupiah(report.total_omzet)}</div></div>
          <div className="p-3 rounded bg-rose-50"><div className="text-xs text-rose-700">Pengeluaran</div><div className="font-bold num text-rose-700">{rupiah(report.total_expenses)}</div></div>
          <div className="p-3 rounded bg-amber-50"><div className="text-xs text-amber-700">SETORAN TUNAI</div><div className="font-bold num text-amber-700" data-testid="daily-setoran">{rupiah(report.setoran_tunai)}</div></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="font-semibold text-sm mb-2">Pembayaran</div>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b"><td className="py-1">Cash</td><td className="text-right num">{rupiah(report.cash_sales)}</td></tr>
                <tr className="border-b"><td className="py-1">QRIS</td><td className="text-right num">{rupiah(report.qris_sales)}</td></tr>
                {report.online_sales > 0 && <tr className="border-b"><td className="py-1">Online</td><td className="text-right num">{rupiah(report.online_sales)}</td></tr>}
                {report.other_sales > 0 && <tr className="border-b"><td className="py-1">Lainnya</td><td className="text-right num">{rupiah(report.other_sales)}</td></tr>}
                <tr className="font-bold"><td className="py-1">Total</td><td className="text-right num">{rupiah(report.total_omzet)}</td></tr>
              </tbody>
            </table>
            <div className="text-xs text-slate-500 mt-3">Jumlah trx: <b>{report.transaction_count}</b> · Item terjual: <b>{report.item_total_qty}</b></div>
          </div>
          <div>
            <div className="font-semibold text-sm mb-2">Pengeluaran Outlet</div>
            <table className="w-full text-sm">
              <tbody>
                {report.expense_details?.map((e,i) => (
                  <tr key={i} className="border-b"><td className="py-1">{e.category}<div className="text-xs text-slate-400">{e.notes}</div></td><td className="text-right num">{rupiah(e.amount)}</td></tr>
                ))}
                {!report.expense_details?.length && <tr><td colSpan="2" className="py-2 text-slate-400 text-center">Tidak ada</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4">
          <div className="font-semibold text-sm mb-2">Detail Item Terjual</div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 border-b"><tr><th className="text-left py-2">Menu</th><th className="text-right">Qty</th><th className="text-right">Subtotal</th></tr></thead>
            <tbody>
              {report.item_details?.map(it => (
                <tr key={it.product_id} className="border-b"><td className="py-1">{it.name}</td><td className="text-right num">{it.qty}</td><td className="text-right num">{rupiah(it.subtotal)}</td></tr>
              ))}
              {!report.item_details?.length && <tr><td colSpan="3" className="py-4 text-center text-slate-400">Tidak ada penjualan</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function JournalTab() {
  const [rows, setRows] = useState([]);
  const [coa, setCOA] = useState([]);
  const [open, setOpen] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    outlet_id: "", description: "", reference: "",
    lines: [{ account_id: "", debit: 0, credit: 0, description: "" }, { account_id: "", debit: 0, credit: 0, description: "" }],
  });

  const load = () => api.get("/journal?limit=200").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/coa").then(({ data }) => setCOA(data));
    api.get("/outlets").then(({ data }) => setOutlets(data));
  }, []);

  function updLine(i, k, v) { setForm({ ...form, lines: form.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }); }
  function addLine() { setForm({ ...form, lines: [...form.lines, { account_id: "", debit: 0, credit: 0, description: "" }] }); }
  function delLine(i) { setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) }); }

  const debitSum = form.lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const creditSum = form.lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = Math.round(debitSum * 100) === Math.round(creditSum * 100);

  async function save() {
    if (!balanced) return toast.error(`Tidak balance: DR ${debitSum} ≠ CR ${creditSum}`);
    if (debitSum <= 0) return toast.error("Total harus > 0");
    try {
      await api.post("/journal", { ...form, outlet_id: form.outlet_id || null });
      toast.success("Jurnal tersimpan");
      setOpen(false);
      setForm({ date: new Date().toISOString().slice(0, 10), outlet_id: "", description: "", reference: "",
                lines: [{ account_id: "", debit: 0, credit: 0, description: "" }, { account_id: "", debit: 0, credit: 0, description: "" }] });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">{rows.length} entri (200 terbaru). Auto-journal dari POS, expense, payroll, kasbon otomatis dipost setiap 30 detik.</div>
        <Button data-testid="journal-new-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Jurnal Manual</Button>
      </div>
      <Card className="p-4">
        <div className="max-h-[600px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 border-b sticky top-0 bg-white">
              <tr><th className="text-left py-2">Tanggal</th><th className="text-left">Deskripsi</th><th className="text-left">Referensi</th><th className="text-right">Total</th><th>Source</th></tr>
            </thead>
            <tbody>
              {rows.map(j => (
                <tr key={j.id} className="border-b hover:bg-slate-50">
                  <td className="py-2">{j.date}</td>
                  <td>
                    <div className="font-medium">{j.description}</div>
                    <div className="text-xs text-slate-500">
                      {j.lines?.map((l,i) => <span key={i}>{l.account_code} {l.debit>0?`DR ${rupiah(l.debit)}`:`CR ${rupiah(l.credit)}`}{i<j.lines.length-1?" · ":""}</span>)}
                    </div>
                  </td>
                  <td className="text-xs font-mono">{j.reference}</td>
                  <td className="text-right num font-bold">{rupiah(j.total)}</td>
                  <td><Badge variant="outline" className={j.source==="auto" ? "text-blue-600" : "text-slate-600"}>{j.source}</Badge></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan="5" className="py-8 text-center text-slate-400">Belum ada jurnal</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Jurnal Manual</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-xs">Tanggal</label><Input type="date" value={form.date} onChange={e=>setForm({...form,date:e.target.value})}/></div>
              <div><label className="text-xs">Outlet (opsional)</label>
                <Select value={form.outlet_id || "none"} onValueChange={v=>setForm({...form,outlet_id: v==="none"?"":v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="none">-</SelectItem>{outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><label className="text-xs">Referensi</label><Input value={form.reference} onChange={e=>setForm({...form,reference:e.target.value})}/></div>
            </div>
            <div><label className="text-xs">Deskripsi</label><Input value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
            <div className="border rounded p-3">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500 mb-1">
                <div className="col-span-4">Akun</div><div className="col-span-2">Debit</div><div className="col-span-2">Kredit</div><div className="col-span-3">Keterangan</div><div className="col-span-1"></div>
              </div>
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center mb-1">
                  <Select value={l.account_id} onValueChange={v=>updLine(i,"account_id",v)}>
                    <SelectTrigger data-testid={`jl-acc-${i}`} className="col-span-4 h-9"><SelectValue placeholder="Pilih akun"/></SelectTrigger>
                    <SelectContent>{coa.map(a=><SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className="col-span-2 num" type="number" value={l.debit} onChange={e=>updLine(i,"debit",Number(e.target.value))}/>
                  <Input className="col-span-2 num" type="number" value={l.credit} onChange={e=>updLine(i,"credit",Number(e.target.value))}/>
                  <Input className="col-span-3" value={l.description} onChange={e=>updLine(i,"description",e.target.value)}/>
                  <Button variant="outline" size="sm" onClick={()=>delLine(i)} className="col-span-1 text-rose-600"><Trash2 className="w-3 h-3"/></Button>
                </div>
              ))}
              <div className="grid grid-cols-12 gap-2 mt-2 text-sm font-bold">
                <div className="col-span-4"><Button size="sm" variant="outline" onClick={addLine}><Plus className="w-3 h-3 mr-1"/>Line</Button></div>
                <div className="col-span-2 num text-right">{rupiah(debitSum)}</div>
                <div className="col-span-2 num text-right">{rupiah(creditSum)}</div>
                <div className={`col-span-4 ${balanced ? "text-emerald-600" : "text-rose-600"}`}>{balanced ? "✓ Balanced" : "✗ Tidak balance"}</div>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="journal-save-btn" onClick={save} disabled={!balanced} className="bg-orange-500 hover:bg-orange-600">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function COATab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", type: "asset", normal_side: "debit" });

  const load = () => api.get("/coa").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  async function save() {
    try { await api.post("/coa", form); toast.success("Akun tersimpan"); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }

  const grouped = rows.reduce((a, r) => { (a[r.type] = a[r.type] || []).push(r); return a; }, {});

  return (
    <div className="mt-4 space-y-3">
      <div className="flex justify-end"><Button data-testid="coa-new-btn" onClick={()=>setOpen(true)} className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Tambah Akun</Button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Object.entries(grouped).map(([type, list]) => (
          <Card key={type} className="p-4">
            <div className="font-bold uppercase text-xs text-slate-500 mb-2">{type}</div>
            {list.map(a => (
              <div key={a.id} className="flex justify-between py-1 border-b text-sm">
                <span className="font-mono text-xs text-slate-400 w-12">{a.code}</span>
                <span className="flex-1">{a.name}</span>
                <Badge variant="outline" className="text-xs">{a.normal_side}</Badge>
              </div>
            ))}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Akun Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs">Kode</label><Input value={form.code} onChange={e=>setForm({...form,code:e.target.value})}/></div>
            <div><label className="text-xs">Nama</label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs">Tipe</label>
                <Select value={form.type} onValueChange={v=>setForm({...form,type:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["asset","liability","equity","revenue","expense"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><label className="text-xs">Normal Side</label>
                <Select value={form.normal_side} onValueChange={v=>setForm({...form,normal_side:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="debit">Debit</SelectItem><SelectItem value="credit">Credit</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button onClick={save} className="bg-orange-500 hover:bg-orange-600">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
